import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, Flame, Gauge, Lock, ShieldCheck, Users } from "lucide-react";
import { api, canUse, LEVEL_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PremiumDialog } from "@/components/PremiumDialog";

const ICONS = { express: Gauge, nom002: Flame, nom019: Users, iso9001: Award, nom002x: Flame, iso9001x: Award };

const ModuleCard = ({ m, i, locked, onLocked }) => {
  const Icon = ICONS[m.id] ?? Gauge;
  const cls = `group relative h-full w-full text-left flex flex-col justify-between gap-8 border bg-[#111722] p-6 sm:p-7 overflow-hidden transition-colors clip-notch ${m.level === "pro" ? "border-[#F59E0B]/35 hover:border-[#F59E0B]" : "border-white/10 hover:border-[#F59E0B]/60"}`;
  const body = (
    <>
        <span className="absolute inset-x-0 top-0 h-[3px] bg-[#F59E0B] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
        <div>
          <div className="flex items-start justify-between gap-4">
            <Icon className="w-7 h-7 text-[#F59E0B]" strokeWidth={1.5} />
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#F5F1E8]/60">{m.total} reactivos · {m.block_count ?? m.blocks?.length ?? 0} bloques</span>
          </div>
          {m.id === "express" && <span className="mt-6 inline-block px-2.5 py-1 bg-[#F59E0B] text-[#0B0F17] font-mono text-[10px] uppercase tracking-[0.16em]">Evaluación rápida</span>}
          {m.level !== "free" && (
            <span className={`mt-6 inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] ${locked ? "bg-[#F59E0B] text-[#0B0F17]" : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"}`} data-testid={`module-premium-badge-${m.id}`}>
              {locked ? <><Lock className="w-3 h-3" /> {LEVEL_LABEL[m.level]}</> : <><ShieldCheck className="w-3 h-3" /> Incluido en tu plan</>}
            </span>
          )}
          <h3 className={`font-display font-extrabold tracking-tight leading-tight ${m.id === "express" || m.level !== "free" ? "mt-3" : "mt-6"} text-2xl`}>{m.name}</h3>
          <p className="mt-1 text-[#F59E0B] text-sm font-semibold">{m.subtitle}</p>
          <p className="mt-4 text-sm text-[#F5F1E8]/70 leading-relaxed">{m.description}</p>
        </div>
        <span className="inline-flex items-center gap-2 text-sm font-semibold">
          {locked ? <>Ver detalles del módulo <Lock className="w-4 h-4" /></> : <>Iniciar auditoría <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" /></>}
        </span>
    </>
  );
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="h-full">
      {locked ? (
        <button type="button" onClick={() => onLocked(m)} data-testid={`start-module-${m.id}`} className={cls}>{body}</button>
      ) : (
        <Link to={`/auditoria?modulo=${m.id}`} data-testid={`start-module-${m.id}`} className={cls}>{body}</Link>
      )}
    </motion.div>
  );
};

export const ModuleSelector = () => {
  const [data, setData] = useState({ pillars: [], modules: [] });
  const [tab, setTab] = useState("stps");
  const [promo, setPromo] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/modules").then((r) => setData({ pillars: r.data?.pillars ?? [], modules: Array.isArray(r.data?.modules) ? r.data.modules : [] })).catch(() => {});
  }, []);

  const list = data.modules.filter((m) => m.pillar === tab);

  return (
    <section className="mt-14" data-testid="module-selector">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Catálogo de auditorías</div>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl font-bold">Elige qué evaluar</h2>
        </div>
        <div className="relative flex p-1 border border-white/15 rounded-full w-full sm:w-fit" role="tablist" data-testid="pillar-tabs">
          {data.pillars.map((p) => (
            <button key={p.id} role="tab" aria-selected={tab === p.id} onClick={() => setTab(p.id)} data-testid={`pillar-tab-${p.id}`}
              className={`relative flex-1 sm:flex-none px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${tab === p.id ? "text-[#0B0F17]" : "text-[#F5F1E8]/75 hover:text-white"}`}>
              {tab === p.id && <motion.span layoutId="pillar-pill" className="absolute inset-0 rounded-full bg-[#F59E0B]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative">{p.title}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid={`module-grid-${tab}`}>
        {list.map((m, i) => <ModuleCard key={m.id} m={m} i={i} locked={!canUse(user, m.level)} onLocked={setPromo} />)}
      </div>
      <PremiumDialog module={promo} onOpenChange={(o) => { if (!o) setPromo(null); }} />
    </section>
  );
};
